// The IP Case Sheet — the clinical workspace for an admitted patient.
//
// ── Shape of the screen ──────────────────────────────────────────
//
//   sticky header      who, where, how long, safety flags
//   alert strip        deterministic alerts, red never collapsed
//   snapshot           the thirty-second read, every claim traceable
//   gist               AI, collapsed, cited, advisory
//   section tabs       critical tabs in a strip, the rest under More
//                      (CaseSheetTabNav), each loading on first open
//
// ── Loading ──────────────────────────────────────────────────────
//
// First paint costs TWO requests: the aggregated case sheet and Today's
// timeline. Every other section is `enabled`-gated on its tab being the active
// one, so opening a patient never pays for eleven sections nobody looked at.
// `visited` keeps a tab mounted once opened, so flipping back is instant.
//
// ── Read-only after discharge ────────────────────────────────────
//
// The record stays fully readable forever; only writes stop. That is enforced
// server-side (422 on any clinical write against a discharged admission) — the
// `readOnly` prop passed down is the UI half, not the guarantee.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { LoadingState, ErrorState } from '@/components/ui/States';
import { useCaseSheet, useClinicalGist } from './hooks';
import type { SourceRef } from './types';
import { PatientHeader } from './PatientHeader';
import { AlertStrip } from './AlertStrip';
import { ClinicalSnapshot } from './ClinicalSnapshot';
import { ClinicalGistCard } from './ClinicalGistCard';
import { useAiAvailability } from '@/features/ai-assist/useAiAvailability';
import { TodayView } from './sections/TodayView';
import { FaceSheetSection } from './sections/FaceSheetSection';
import { AssessmentSection } from './sections/AssessmentSection';
import { ProgressSection } from './sections/ProgressSection';
import { NursingNotesSection, FluidSection } from './sections/NursingSection';
import { MedicationSection } from './sections/MedicationSection';
import { VitalsSection } from './sections/VitalsSection';
import { InvestigationsSection, ProceduresSection, ConsultsSection, CarePlanSection } from './sections/OrdersSections';
import { ConsentsSection } from './sections/ConsentsSection';
import { RiskScreensSection } from './sections/RiskScreensSection';
import { ClinicalOrdersSection } from './sections/ClinicalOrdersSection';
import { DischargeSummarySection } from './sections/DischargeSummarySection';
import { SpecialtyRecordsSection } from './sections/SpecialtyRecordsSection';
import { TimelineSection } from './sections/TimelineSection';
import { useCan } from './ui';
import { CASE_SHEET_TABS, visibleTabs, sourceFocus, type FocusRequest, type TabId } from './caseSheetTabs';
import { focusInPanel } from './focusTarget';
import { Plus } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { CaseSheetTabNav } from './CaseSheetTabNav';


export function CaseSheetWorkspace({ admissionId, onClose, openDischargeOn, stickyOffsetPx }: {
  admissionId: number; onClose?: () => void;
  // A changing value (not a boolean — this component can stay mounted
  // across many separate "open it" requests from IPModule.tsx's Charges &
  // discharge screen) that jumps straight to the Discharge tab with the AI
  // assistant already open. Undefined/0 on first mount does nothing.
  openDischargeOn?: number;
  /** The height (px) of a sticky toolbar the caller renders directly above
   * this workspace (IPModule.tsx's "← Back to IP / Wards" row) — forwarded
   * to PatientHeader so its own sticky point lands right below that
   * toolbar instead of under/overlapping it. Omit when nothing sticky sits
   * above this component; PatientHeader defaults to sticking flush at the
   * true top on its own. */
  stickyOffsetPx?: number;
}) {
  const can = useCan();
  // AI off (§4.4): the gist card is hidden entirely — AlertStrip/ClinicalSnapshot
  // (deterministic, no LLM) stay exactly as they are.
  const { aiEnabled } = useAiAvailability();
  const [tab, setTab] = useState<TabId>('today');
  // Sections stay mounted once opened so returning to one is instant, but are
  // never mounted before the clinician asks for them.
  const [visited, setVisited] = useState<Set<TabId>>(new Set(['today']));
  // Owned here, not inside DischargeSummarySection, because opening the AI
  // assistant has to collapse THIS component's own header/alerts/snapshot/
  // tab-strip down to a minimal back-bar — giving the chat the full screen,
  // the same dedicated-step feel IPModule.tsx's discharge flow already has,
  // rather than fighting the structured form for space inside one tab panel.
  const [dischargeChatOpen, setDischargeChatOpen] = useState(false);

  const { data, isLoading, isError, error, refetch } = useCaseSheet(admissionId);
  const gist = useClinicalGist(admissionId);

  const openTab = useCallback((id: TabId) => {
    setTab(id);
    setVisited(v => (v.has(id) ? v : new Set(v).add(id)));
  }, []);

  // A drill-through ("Open record", an MRD checklist item, the code-status
  // pill) opens its tab AND scrolls to the record or field — switching the
  // tab alone left the user at the top of the page (on a phone, still looking
  // at the header) with no sign anything had happened.
  const rootRef = useRef<HTMLDivElement>(null);
  const [focusReq, setFocusReq] = useState<(FocusRequest & { seq: number }) | null>(null);
  const openFocus = useCallback((req: FocusRequest) => {
    openTab(req.tab);
    setFocusReq(prev => ({ ...req, seq: (prev?.seq ?? 0) + 1 }));
  }, [openTab]);
  useEffect(() => {
    if (!focusReq || !rootRef.current) return;
    return focusInPanel(rootRef.current, focusReq.tab, focusReq.targets, stickyOffsetPx ?? 0);
  }, [focusReq, stickyOffsetPx]);

  const openSource = useCallback((ref: SourceRef) => {
    const req = sourceFocus(ref);
    if (req) openFocus(req);
  }, [openFocus]);

  const prevOpenDischargeOn = useRef(openDischargeOn);
  useEffect(() => {
    if (openDischargeOn === undefined || openDischargeOn === prevOpenDischargeOn.current) return;
    prevOpenDischargeOn.current = openDischargeOn;
    openTab('discharge');
    setDischargeChatOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDischargeOn]);

  // A different patient is a different record: reset to Today rather than
  // leaving the previous patient's open tab selected.
  useEffect(() => {
    setTab('today');
    setVisited(new Set(['today']));
    setFocusReq(null);
    gist.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admissionId]);

  const readOnly = data?.admission.status !== 'admitted';

  // A tab with its own grant is absent, not disabled, for a user without it
  // (none sets one today — see caseSheetTabs.ts).
  const tabs = useMemo(() => visibleTabs(CASE_SHEET_TABS, can), [can]);

  // Quick actions are filtered by grant — an action the user cannot perform is
  // absent, not disabled.
  const quickActions = useMemo(() => ([
    // Round-time entries first, then the once-per-stay / event-driven ones.
    { label: 'Progress note', tab: 'progress' as TabId, module: 'ip:progress-note' },
    { label: 'Vitals', tab: 'vitals' as TabId, module: 'ip:vitals' },
    { label: 'Medication order', tab: 'medications' as TabId, module: 'ip:med-order' },
    { label: 'Order', tab: 'orders' as TabId, module: 'ip:clinical-order' },
    { label: 'Nursing note', tab: 'nursing' as TabId, module: 'ip:nursing-note' },
    { label: 'Investigation', tab: 'investigations' as TabId, module: 'ip:investigation' },
    { label: 'Fluid balance', tab: 'fluids' as TabId, module: 'ip:fluid-balance' },
    { label: 'Risk screen', tab: 'risk-screens' as TabId, module: 'ip:risk-screen' },
    { label: 'Consult', tab: 'consults' as TabId, module: 'ip:consult' },
    { label: 'Consent', tab: 'consents' as TabId, module: 'ip:consent' },
    { label: 'Care task', tab: 'care-plan' as TabId, module: 'ip:care-plan' },
    { label: 'Attender', tab: 'face-sheet' as TabId, module: 'ip:admission-record' },
    { label: 'Specialty record', tab: 'specialty' as TabId, module: 'ip:specialty-record' },
    { label: 'Discharge summary', tab: 'discharge' as TabId, module: 'ip:discharge-summary' },
  ]).filter(a => can(a.module)), [can]);

  if (isLoading) return <LoadingState label="Opening case sheet…" />;
  if (isError || !data) {
    return (
      <ErrorState
        message={error instanceof Error ? error.message : 'This case sheet could not be loaded.'}
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <div ref={rootRef} className="ip-case-sheet space-y-4">
      {dischargeChatOpen ? (
        // Mirrors IPModule.tsx's dedicated discharge-summary step: a single
        // slim identity line + a back control, nothing else — the chat gets
        // everything the header/alerts/snapshot/gist/tab-strip would
        // otherwise take up.
        <div className="flex items-center justify-between gap-3 border-b border-border pb-2">
          <div>
            <div className="text-[15px] font-bold">{data.patient.name} — Discharge summary</div>
            <div className="text-[12px] text-ink-muted">
              {data.patient.uhid} · {data.admission.ward_name}, bed {data.admission.bed_number}
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setDischargeChatOpen(false)}>← Back</Button>
        </div>
      ) : (
        <>
          <PatientHeader data={data} onClose={onClose} onOpenFocus={openFocus} stickyOffsetPx={stickyOffsetPx} />

          <AlertStrip alerts={data.alerts} onOpen={openSource} />

          <ClinicalSnapshot data={data} onOpen={openSource} />

          {aiEnabled && (
            <ClinicalGistCard
              gist={gist.data}
              isPending={gist.isPending}
              error={gist.error as Error | null}
              onGenerate={() => gist.mutate()}
              onOpen={openSource}
            />
          )}

          {/* Quick add: one menu instead of a row of fourteen buttons (which
              wrapped to five or six rows on a phone). Each entry opens the
              section where that record is written. */}
          {/* On a phone Add gets its own row above the strip, so the strip
              keeps its full width for tabs. */}
          <div className="flex items-end gap-2 max-sm:flex-col-reverse max-sm:items-stretch">
            <div className="min-w-0 flex-1">
              <CaseSheetTabNav tabs={tabs} active={tab} onSelect={openTab} />
            </div>
            {!readOnly && quickActions.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="primary" size="sm" className="mb-1 shrink-0 self-end pointer-coarse:min-h-11" aria-label="Add a record">
                    <Plus aria-hidden className="size-4" /> Add
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" collisionPadding={8} className="w-[min(14rem,calc(100vw-16px))]">
                  {quickActions.map(a => (
                    <DropdownMenuItem key={a.label} onSelect={() => openTab(a.tab)} className="text-[13px] pointer-coarse:py-3">
                      {a.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </>
      )}

      <div>
        {visited.has('face-sheet') && (
          <div data-tab-panel="face-sheet" hidden={tab !== 'face-sheet'}>
            <FaceSheetSection admissionId={admissionId} readOnly={readOnly} />
          </div>
        )}
        {visited.has('today') && (
          <div data-tab-panel="today" hidden={tab !== 'today'}>
            <TodayView admissionId={admissionId} caseSheet={data} onOpen={openSource} />
          </div>
        )}
        {visited.has('medications') && (
          <div data-tab-panel="medications" hidden={tab !== 'medications'}>
            <MedicationSection admissionId={admissionId} readOnly={readOnly} />
          </div>
        )}
        {visited.has('vitals') && (
          <div data-tab-panel="vitals" hidden={tab !== 'vitals'}>
            <VitalsSection admissionId={admissionId} readOnly={readOnly} />
          </div>
        )}
        {visited.has('progress') && (
          <div data-tab-panel="progress" hidden={tab !== 'progress'}>
            <ProgressSection admissionId={admissionId} readOnly={readOnly} />
          </div>
        )}
        {visited.has('orders') && (
          <div data-tab-panel="orders" hidden={tab !== 'orders'}>
            <ClinicalOrdersSection admissionId={admissionId} readOnly={readOnly} />
          </div>
        )}
        {visited.has('nursing') && (
          <div data-tab-panel="nursing" hidden={tab !== 'nursing'}>
            <NursingNotesSection admissionId={admissionId} readOnly={readOnly} />
          </div>
        )}
        {visited.has('fluids') && (
          <div data-tab-panel="fluids" hidden={tab !== 'fluids'}>
            <FluidSection admissionId={admissionId} readOnly={readOnly} />
          </div>
        )}
        {visited.has('assessment') && (
          <div data-tab-panel="assessment" hidden={tab !== 'assessment'}>
            <AssessmentSection admissionId={admissionId} readOnly={readOnly} caseSheet={data} />
          </div>
        )}
        {visited.has('risk-screens') && (
          <div data-tab-panel="risk-screens" hidden={tab !== 'risk-screens'}>
            <RiskScreensSection admissionId={admissionId} readOnly={readOnly} />
          </div>
        )}
        {visited.has('investigations') && (
          <div data-tab-panel="investigations" hidden={tab !== 'investigations'}>
            <InvestigationsSection admissionId={admissionId} readOnly={readOnly} />
          </div>
        )}
        {visited.has('procedures') && (
          <div data-tab-panel="procedures" hidden={tab !== 'procedures'}>
            <ProceduresSection admissionId={admissionId} readOnly={readOnly} />
          </div>
        )}
        {visited.has('consults') && (
          <div data-tab-panel="consults" hidden={tab !== 'consults'}>
            <ConsultsSection admissionId={admissionId} readOnly={readOnly} />
          </div>
        )}
        {visited.has('consents') && (
          <div data-tab-panel="consents" hidden={tab !== 'consents'}>
            <ConsentsSection admissionId={admissionId} readOnly={readOnly} />
          </div>
        )}
        {visited.has('care-plan') && (
          <div data-tab-panel="care-plan" hidden={tab !== 'care-plan'}>
            <CarePlanSection admissionId={admissionId} readOnly={readOnly} />
          </div>
        )}
        {visited.has('discharge') && (
          <div data-tab-panel="discharge" hidden={tab !== 'discharge'}>
            <DischargeSummarySection
              admissionId={admissionId} readOnly={readOnly} patientUhid={data.patient.uhid}
              chatOpen={dischargeChatOpen} onChatOpenChange={setDischargeChatOpen}
            />
          </div>
        )}
        {visited.has('specialty') && (
          <div data-tab-panel="specialty" hidden={tab !== 'specialty'}>
            <SpecialtyRecordsSection admissionId={admissionId} readOnly={readOnly} />
          </div>
        )}
        {visited.has('timeline') && (
          <div data-tab-panel="timeline" hidden={tab !== 'timeline'}>
            <TimelineSection admissionId={admissionId} onOpen={openSource} />
          </div>
        )}
      </div>
    </div>
  );
}
